import '../css/live-streaming-advertisement-manager.css';
/**
 * Enhanced Advertisement Manager System
 * 
 * A unified advertisement management system for both movie playback and live streaming
 * that works with the Plyr.io media player.
 * 
 * HTML Ad Types:
 * - fullscreen: Video ads that cover the entire player area
 * - companion:  Banner-style ads with logo, title, description, image
 * - overlay:    Small overlay ads that appear on top of content
 */

class LS_AdvertisementManager {
    /**
     * Constructor for the Advertisement Manager
     * @param {Object} player  - Plyr.io player instance
     * @param {Object} options - Configuration options
     */
    constructor(player, options = {}) {
        // Core properties
        this.player = player;
        // Get the main Plyr container element
        const container = this.player.elements?.container || this.player; // Fallback if `elements.container` doesn't exist

        // Create ad containers 
        const adContainer     = document.createElement('div');
        adContainer.className = 'ls-ad-container';

        const companionContainer     = document.createElement('div');
        companionContainer.className = 'ls-companion-ad-container';

        // Append to the main Plyr container
        container.appendChild(adContainer);
        container.appendChild(companionContainer);

        this.videoElement  = player.elements.original;
        this.isLiveStream  = options.isLiveStream || false;
        this.adQueue       = [];
        this.currentAd     = null;
        this.contentPaused = false;
        this.adPlaying     = false;

        // Ad configuration with specific pre-roll, mid-roll and post-roll collections
        this.adConfig = {
            vastAdUrls: {
                preroll:  options.vastAdUrls?.preroll || [],
                midroll:  options.vastAdUrls?.midroll || [],
                postroll: options.vastAdUrls?.postroll || [],
                any:      options.vastAdUrls?.any || options.vastAdUrls || []
            },
            htmlAds: {
                preroll:  options.htmlAds?.preroll || [],
                midroll:  options.htmlAds?.midroll || [],
                postroll: options.htmlAds?.postroll || [],
                any:      options.htmlAds?.any || []
            },
            adFrequency:         options.adFrequency || parseInt(options.element?.getAttribute('data-ad-frequency')) || 5,
            lastAdTime:          0,
            loopAds:             this.isLiveStream,
            prerollEnabled:      options.prerollEnabled !== undefined ? options.prerollEnabled : true,
            midrollEnabled:      options.midrollEnabled !== undefined ? options.midrollEnabled : true,
            postrollEnabled:     options.postrollEnabled !== undefined ? options.postrollEnabled : true,
            adContainerSelector: options.adContainerSelector || null,
            imaSettings:         options.imaSettings || {}
        };

        // Get DOM containers (should be created by PHP)
        this.adContainer = this.getAdContainer();

        this.companionContainer = this.getCompanionContainer();
        // Check if we have VAST ads
        this.hasVastAds  = this.checkForVastAds();

        // Initialize events
        this.setupEventListeners();

        // Track midroll ad plays for non-livestreams
        this.midrollPlaysCount = 1;
        this.initialized       = false;
    }

    /**
     * Get ad container element from DOM (created by PHP)
     */
    getAdContainer() {
        return this.player.elements?.container?.querySelector('.ls-ad-container') || null;
    }

    /**
     * Get companion container element from DOM (created by PHP)
     */
    getCompanionContainer() {
        return this.player.elements?.container?.querySelector('.ls-companion-ad-container') || null;
    }

    /**
     * Check if we have any VAST ads configured
     */
    checkForVastAds() {
        const vastUrls = this.adConfig.vastAdUrls;
        return Object.keys(vastUrls).some(key =>
            Array.isArray(vastUrls[key]) && vastUrls[key].length > 0
        );
    }

    /**
     * Set up event listeners for player and ads
     */
    setupEventListeners() {
        // Use player events more efficiently
        this.player.on('playing', () => this.onContentPlaying());
        this.player.on('timeupdate', () => this.checkForMidrollAds());
        this.player.on('ended', () => this.onContentEnded());

        // Custom ad events
        this.videoElement.addEventListener('adEnded', () => this.onAdEnded());
    }

    /**
     * Initialize ad system when content starts playing
     */
    onContentPlaying() {
        // Initialize only once
        if (this.initialized) return;
        this.initialized = true;

        if (this.isLiveStream) {
            this.setupLivestreamAds();
        } else {
            this.setupMovieAds();
        }

        // Handle preroll ads if enabled and not already played
        if (!this.prerollPlayed && this.adConfig.prerollEnabled) {
            this.prerollPlayed = true;
            this.playPrerollAd();
        }
    }

    /**
     * Play preroll advertisement
     */
    playPrerollAd() {
        if (this.adPlaying) return;

        const adToPlay = this.selectAd('preroll');
        if (!adToPlay) return;

        this.pauseContent();
        this.playAd(adToPlay);
    }

    /**
     * Play a midroll advertisement
     */
    playMidrollAd() {

        if (this.adPlaying) return;

        const adToPlay = this.selectAd('midroll');

        if (!adToPlay) return;

        // Increment midroll counter
        this.midrollPlaysCount = (this.midrollPlaysCount || 0) + 1;

        // If this is an overlay ad, we don't need to pause content
        if (adToPlay.overlay !== true) {
            this.pauseContent();
        }

        this.playAd(adToPlay);
    }

    /**
     * Play postroll advertisement
     */
    playPostrollAd() {

        if (this.adPlaying) return;

        const adToPlay = this.selectAd('postroll');
        if (!adToPlay) return;

        this.pauseContent();
        this.playAd(adToPlay);
    }

    /**
     * Select an appropriate ad for playback
     */
    selectAd(adType) {
        let adList = [];
        let adIndex = -1;

        // For midroll ads, use consistent ordering
        if (adType === 'midroll') {
            const playedCount = (this.midrollPlaysCount || 1) - 1;

            // Try type-specific HTML ads first
            const typeSpecificHtmlAds = this.adConfig.htmlAds[adType];
            if (typeSpecificHtmlAds && typeSpecificHtmlAds.length > 0) {
                adList = typeSpecificHtmlAds;

                if (this.isLiveStream) {
                    adIndex = playedCount % adList.length;
                } else {
                    if (playedCount < adList.length) {
                        adIndex = playedCount;
                    } else {
                        return null;
                    }
                }

                if (adIndex >= 0) {
                    return this.formatHtmlAd(adList[adIndex], adType);
                }
            }

            // Try type-specific VAST ads
            const typeSpecificVastAds = this.adConfig.vastAdUrls[adType];
            if (typeSpecificVastAds && typeSpecificVastAds.length > 0) {
                adList = typeSpecificVastAds;

                if (this.isLiveStream) {
                    adIndex = playedCount % adList.length;
                } else {
                    if (playedCount < adList.length) {
                        adIndex = playedCount;
                    } else {
                        return null;
                    }
                }

                if (adIndex >= 0) {
                    return {
                        type: 'vast',
                        adType: adType,
                        url: adList[adIndex]
                    };
                }
            }

            return null;
        } else {
            // For non-midroll ads, pick randomly
            const htmlAds = this.adConfig.htmlAds[adType];
            if (htmlAds && htmlAds.length > 0) {
                const selectedAd = htmlAds[Math.floor(Math.random() * htmlAds.length)];
                return this.formatHtmlAd(selectedAd, adType);
            }

            const vastAds = this.adConfig.vastAdUrls[adType];
            if (vastAds && vastAds.length > 0) {
                return {
                    type: 'vast',
                    adType: adType,
                    url: vastAds[Math.floor(Math.random() * vastAds.length)]
                };
            }

            // Try 'any' ads as fallback
            const anyHtmlAds = this.adConfig.htmlAds.any;
            if (anyHtmlAds && anyHtmlAds.length > 0) {
                const selectedAd = anyHtmlAds[Math.floor(Math.random() * anyHtmlAds.length)];
                return this.formatHtmlAd(selectedAd, adType);
            }

            const anyVastAds = this.adConfig.vastAdUrls.any;
            if (anyVastAds && anyVastAds.length > 0) {
                return {
                    type: 'vast',
                    adType: adType,
                    url: anyVastAds[Math.floor(Math.random() * anyVastAds.length)]
                };
            }

            return null;
        }
    }

    /**
     * Format HTML ad object
     */
    formatHtmlAd(ad, adType) {
        return {
            type:         'html',
            adType:       adType,
            adFormat:     ad.adFormat || 'fullscreen',
            content:      ad.content,
            title:        ad.title,
            description:  ad.description,
            imageUrl:     ad.imageUrl,
            ctaText:      ad.ctaText,
            ctaUrl:       ad.ctaUrl,
            videoUrl:     ad.videoUrl,
            duration:     ad.duration || 15,
            position:     ad.position || 'bottom-right',
            campaign:     ad.campaign || null,
            skipenabled:  ad.skipenabled || false,
            skipDuration: ad.skipDuration,
            overlay:      ad.overlay
        };
    }

    /**
     * Play the selected advertisement
     */
    playAd(ad) {
        this.adPlaying = true;
        this.currentAd = ad;

        if (ad.type === 'vast') {
            this.playVastAd(ad);
        } else if (ad.type === 'html') {
            this.playHtmlAd(ad);
        }

        this.trackAdImpression(ad);

        // Dispatch ad started event
        const adStartEvent = new CustomEvent('ls-ad-started', {
            detail: {
                adType: ad.adType,
                adFormat: ad.adFormat || ad.type
            }
        });
        this.videoElement.dispatchEvent(adStartEvent);
        document.dispatchEvent(adStartEvent);
    }

    /**
     * Play a VAST advertisement
     */
    playVastAd(ad) {
        // Only load IMA SDK if needed and available        
        if (this.hasVastAds && !this.imaLoaded) {
            this.loadImaSDK().then(() => {
                this.playVastAdWithIma(ad);
            }).catch(() => {
                console.warn('Failed to load IMA SDK, skipping VAST ad');
                this.onAdEnded();
            });
        } else if (this.imaLoaded) {
            this.playVastAdWithIma(ad);
        } else {
            console.warn('No VAST ads configured, skipping');
            this.onAdEnded();
        }
    }

    /**
     * Load Google IMA SDK dynamically
     */
    loadImaSDK() {
        return new Promise((resolve, reject) => {
            if (typeof google !== 'undefined' && google.ima) {
                this.imaLoaded = true;
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
            script.async = true;
            script.onload = () => {
                this.imaLoaded = true;
                resolve();
            };
            script.onerror = () => {
                reject(new Error('Failed to load IMA SDK'));
            };
            document.head.appendChild(script);
        });
    }

    /**
     * Play VAST ad with Google IMA SDK
     */
    playVastAdWithIma(ad) {
        if (!this.adContainer) {
            console.error('No ad container available');
            this.onAdEnded();
            return;
        }

        // Show ad container
        this.adContainer.style.display = 'block';
        this.adContainer.innerHTML = '';

        // Create IMA ad container
        const adVideoElement = document.createElement('div');
        adVideoElement.className = 'ls-ima-ad-container';
        this.adContainer.appendChild(adVideoElement);

        // Initialize IMA
        this.setupIma(adVideoElement, ad);
    }

    /**
     * Setup Google IMA for VAST ads
     */
    setupIma(adContainerEl, ad) {
        try {
            // Create ad display container
            this.adDisplayContainer = new google.ima.AdDisplayContainer(
                adContainerEl,
                this.videoElement
            );

            // Create ads loader
            this.adsLoader = new google.ima.AdsLoader(this.adDisplayContainer);

            // Add event listeners
            this.adsLoader.addEventListener(
                google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
                (e) => this.onAdsManagerLoaded(e),
                false
            );

            this.adsLoader.addEventListener(
                google.ima.AdErrorEvent.Type.AD_ERROR,
                () => this.onAdError(),
                false
            );

            // Initialize and request ads
            this.adDisplayContainer.initialize();

            const adsRequest    = new google.ima.AdsRequest();
            adsRequest.adTagUrl = ad.url;

            const width  = this.adContainer.clientWidth;
            const height = this.adContainer.clientHeight;
            adsRequest.linearAdSlotWidth = width;
            adsRequest.linearAdSlotHeight = height;

            this.adsLoader.requestAds(adsRequest);
        } catch (error) {
            console.error('IMA setup error:', error);
            this.onAdError();
        }
    }

    /**
     * Handle ads manager loaded
     */
    onAdsManagerLoaded(adsManagerLoadedEvent) {
        this.adsManager = adsManagerLoadedEvent.getAdsManager(this.videoElement);

        // Add event listeners
        this.adsManager.addEventListener(
            google.ima.AdErrorEvent.Type.AD_ERROR,
            () => this.onAdError()
        );

        this.adsManager.addEventListener(
            google.ima.AdEvent.Type.ALL_ADS_COMPLETED,
            () => this.onAdEnded()
        );

        try {
            const width  = this.adContainer.clientWidth;
            const height = this.adContainer.clientHeight;
            this.adsManager.init(width, height, google.ima.ViewMode.NORMAL);
            this.adsManager.start();
        } catch (error) {
            console.error('AdsManager error:', error);
            this.onAdError();
        }
    }

    /**
     * Play an HTML advertisement based on format type
     */
    playHtmlAd(ad) {
        switch (ad.adFormat) {
            case 'fullscreen':
                this.playFullscreenHtmlAd(ad);
                break;
            case 'companion':
                this.playCompanionHtmlAd(ad);
                break;
            case 'overlay':
                this.playOverlayHtmlAd(ad);
                break;
            default:
                this.playFullscreenHtmlAd(ad);
        }
    }

    /**
     * Play fullscreen HTML advertisement
     */
    playFullscreenHtmlAd(ad) {
        if (!this.adContainer) return;

        this.adContainer.style.display = 'block';
        this.adContainer.innerHTML     = '';

        const fullscreenContainer      = document.createElement('div');
        fullscreenContainer.className  = 'ls-fullscreen-ad';

        if (ad.videoUrl) {
            // Video ad
            const videoElement         = document.createElement('video');
            videoElement.src           = ad.videoUrl;
            videoElement.autoplay      = true;
            videoElement.muted         = false;
            videoElement.controls      = false;
            videoElement.disablePictureInPicture = true;
            videoElement.controlsList  = 'nodownload nofullscreen noremoteplayback';

            fullscreenContainer.appendChild(videoElement);

            videoElement.addEventListener('ended', () => {
                this.onAdEnded();
            });

            videoElement.addEventListener('contextmenu', (e) => {
                e.preventDefault();
            });

            // Handle skip button
            if (ad.skipenabled) {
                this.addSkipButton(fullscreenContainer, ad.skipDuration || 5);
            }

            videoElement.play().catch(() => {
                console.error('Failed to play video ad');
                this.onAdEnded();
            });

        } else if (ad.content) {
            // Custom HTML content
            fullscreenContainer.innerHTML = ad.content;

            if (ad.skipenabled) {
                this.addSkipButton(fullscreenContainer, ad.skipDuration || 5);
            }

            // Auto-close after duration
            this.adTimeout = setTimeout(() => {
                this.onAdEnded();
            }, ad.duration * 1000);
        }

        this.adContainer.appendChild(fullscreenContainer);
        this.makeClickable(fullscreenContainer, ad);
    }

    /**
     * Play companion HTML advertisement
     */
    playCompanionHtmlAd(ad) {
        if (!this.companionContainer) return;

        // Optionally clear existing ad content
        this.companionContainer.innerHTML = '';

        // Append new ad HTML
        if (ad.content) {
            this.companionContainer.innerHTML = ad.content;
        }

        // Fire event to let PHP template render the ad (if needed)
        const renderEvent = new CustomEvent('ls-render-companion-ad', {
            detail: { ad, container: this.companionContainer }
        });
        document.dispatchEvent(renderEvent);

        // Show container
        this.companionContainer.style.display = 'block';

        // Initialize countdown
        const duration = ad.duration || 15;
        const countdownElement = this.companionContainer.querySelector('.ls-ad-countdown');

        if (countdownElement) {
            let timeLeft = duration;
            countdownElement.textContent = timeLeft;

            const countdownInterval = setInterval(() => {
                timeLeft--;
                countdownElement.textContent = timeLeft;

                if (timeLeft <= 0) {
                    clearInterval(countdownInterval);
                    this.hideCompanionAd();
                }
            }, 1000);

            // Store interval for cleanup
            this.adCountdownInterval = countdownInterval;
        }

        // Set timeout to auto-close after duration
        this.adTimeout = setTimeout(() => {
            this.lastAdPlayTime = Date.now();
            this.adConfig.lastAdTime = this.player.currentTime || 0;
            this.hideCompanionAd();
        }, duration * 1000);

        // Resume content for companion ads
        this.resumeContent();
    }

    /**
     * Play overlay HTML advertisement
     */
    playOverlayHtmlAd(ad) {
        if (!this.adContainer) return;

        this.adContainer.classList.add('is-overlay-active');

        if (ad.content) {
            this.adContainer.innerHTML = ad.content;
        }
        this.adContainer.style.display = 'block';

        // Fire event to let PHP template render the overlay
        const renderEvent = new CustomEvent('ls-render-overlay-ad', {
            detail: { ad, container: this.adContainer }
        });
        document.dispatchEvent(renderEvent);

        // Initialize countdown
        const duration = ad.duration || 15;
        const countdownElement = this.adContainer.querySelector('.ls-ad-countdown');
        let countdownInterval;

        if (countdownElement) {
            let timeLeft = duration;
            countdownElement.textContent = timeLeft;

            countdownInterval = setInterval(() => {
                timeLeft--;
                countdownElement.textContent = timeLeft > 0 ? timeLeft : 0;

                if (timeLeft <= 0) {
                    clearInterval(countdownInterval);
                }
            }, 1000);
        }

        // Auto-close after duration
        if (this.adTimeout) {
            clearTimeout(this.adTimeout);
        }
        this.adTimeout = setTimeout(() => {
            if (countdownInterval) {
                clearInterval(countdownInterval);
            }
            this.onAdEnded();
        }, duration * 1000);

        // Resume content for overlay ads
        this.resumeContent();
    }

    /**
     * Add skip button to fullscreen ads
     */
    addSkipButton(container, skipDelay) {
        const skipButton        = document.createElement('button');
        skipButton.className    = 'ls-ad-skip-button';
        skipButton.disabled     = true;

        // Use localized strings 
        const skipText          = LSFrontendAjax.skipText || 'Skip';
        const skipCountdownText = LSFrontendAjax.skipCountdownText || 'Skip in %ss';

        skipButton.textContent  = skipCountdownText.replace('%s', skipDelay);

        let countdown           = skipDelay;
        const countdownInterval = setInterval(() => {
            countdown--;
            if (countdown <= 0) {
                skipButton.disabled = false;
                skipButton.textContent = skipText;
                clearInterval(countdownInterval);
            } else {
                skipButton.textContent = skipCountdownText.replace('%s', countdown);
            }
        }, 1000);

        skipButton.addEventListener('click', () => {
            if (!skipButton.disabled) {
                clearInterval(countdownInterval);
                this.onAdEnded();
            }
        });

        container.appendChild(skipButton);
    }

    /**
     * Hide companion ad
     */
    hideCompanionAd() {

        if (this.companionContainer) {
            this.companionContainer.style.display = 'none';
            this.companionContainer.innerHTML = '';
        }

        this.adPlaying = false;
        this.currentAd = null;

        if (this.adTimeout) {
            clearTimeout(this.adTimeout);
            this.adTimeout = null;
        }
    }

    /**
     * Set up ads for movie playback
     */
    setupMovieAds() {
        const contentDuration = this.player.duration;

        if (!contentDuration || isNaN(contentDuration)) {
            console.warn('Advertisement Manager: Could not determine content duration');
            return;
        }

        if (this.adConfig.midrollEnabled) {
            const segmentSize = contentDuration / (this.adConfig.adFrequency + 1);

            for (let i = 1; i <= this.adConfig.adFrequency; i++) {
                const timePoint = segmentSize * i;
                this.adQueue.push({
                    type: 'midroll',
                    timePoint: timePoint,
                    played: false
                });
            }
        }

        if (this.adConfig.postrollEnabled) {
            this.adQueue.push({
                type: 'postroll',
                timePoint: contentDuration,
                played: false
            });
        }
    }

    /**
     * Set up ads for livestream playback
     */
    setupLivestreamAds() {

        if (this.adConfig.midrollEnabled) {

            const frequency = this.adConfig.adFrequency || 50;

            this.lastAdPlayTime = Date.now();

            this.livestreamAdInterval = setInterval(() => {
                const currentTime = Date.now();
                const timeSinceLastAd = (currentTime - this.lastAdPlayTime) / 1000;

                if (!this.adPlaying && !this.player.paused && timeSinceLastAd >= frequency) {
                    this.playMidrollAd();
                    this.lastAdPlayTime = currentTime;
                }
            }, 1000);
        }
    }

    /**
     * Check for midroll ads during playback
     */
    checkForMidrollAds() {

        if (this.isLiveStream || this.adPlaying || this.player.paused) {
            return;
        }

        const currentTime = this.player.currentTime;
        const timeSinceLastAd = currentTime - this.adConfig.lastAdTime;

        if (timeSinceLastAd >= this.adConfig.adFrequency) {
            const adToPlay = this.selectAd('midroll');
            if (adToPlay) {
                this.playMidrollAd();
                this.adConfig.lastAdTime = currentTime;
            }

        }
    }

    /**
     * Handle content ended
     */
    onContentEnded() {
        if (!this.isLiveStream && this.adConfig.postrollEnabled) {
            this.playPostrollAd();
        }
    }

    /**
     * Make ad elements clickable and trackable
     */
    makeClickable(container, ad) {
        const clickableElements = container.querySelectorAll('a, button, [data-ad-click]');
        clickableElements.forEach(element => {
            element.addEventListener('click', (e) => {
                this.trackAdClick(ad, e);
            });
        });
    }

    /**
     * Handle the end of an advertisement
     */
    onAdEnded() {

        // Clear timeouts
        if (this.adTimeout) {
            clearTimeout(this.adTimeout);
            this.adTimeout = null;
        }

        if (this.skipButtonTimeout) {
            clearTimeout(this.skipButtonTimeout);
            this.skipButtonTimeout = null;
        }

        // Hide containers
        if (this.adContainer) {
            this.adContainer.style.display = 'none';
            this.adContainer.innerHTML = '';
        }

        // Clean up IMA if needed
        if (this.adsManager) {
            this.adsManager.destroy();
            this.adsManager = null;
        }

        // Reset state
        this.adPlaying = false;
        this.lastAdPlayTime = Date.now();

        this.adConfig.lastAdTime = this.player.currentTime || 0;
        // Resume content if needed
        if (this.contentPaused) {
            this.resumeContent();
        }

        this.currentAd = null;

        // Dispatch event
        const adEndedEvent = new CustomEvent('ls-ad-ended');
        this.videoElement.dispatchEvent(adEndedEvent);
        document.dispatchEvent(adEndedEvent);
    }

    /**
     * Handle ad error
     */
    onAdError() {
        console.error('Ad error occurred');
        this.onAdEnded();
    }

    /**
     * Pause content
     */
    pauseContent() {
        if (!this.contentPaused) {
            this.player.pause();
            this.contentPaused = true;
        }
    }

    /**
     * Resume content
     */
    resumeContent() {
        if (this.contentPaused) {
            this.player.play();
            this.contentPaused = false;
        }
    }

    /**
     * Track ad impression
     */
    trackAdImpression(ad) {
        const event = new CustomEvent('ls-ad-impression', {
            detail: {
                type: ad.type,
                adType: ad.adType,
                adFormat: ad.adFormat,
                campaign: ad.campaign || 'unknown',
                timestamp: new Date().toISOString()
            }
        });
        document.dispatchEvent(event);
    }

    /**
     * Track ad click
     */
    trackAdClick(ad, event) {
        const clickEvent = new CustomEvent('ls-ad-click', {
            detail: {
                type: ad.type,
                adType: ad.adType,
                adFormat: ad.adFormat,
                campaign: ad.campaign || 'unknown',
                element: event.target.tagName,
                timestamp: new Date().toISOString()
            }
        });
        document.dispatchEvent(clickEvent);
    }

    /**
     * Clean up resources
     */
    destroy() {

        if (this.livestreamAdInterval) {
            clearInterval(this.livestreamAdInterval);
        }

        if (this.adTimeout) {
            clearTimeout(this.adTimeout);
        }

        if (this.skipButtonTimeout) {
            clearTimeout(this.skipButtonTimeout);
        }

        if (this.adsManager) {
            this.adsManager.destroy();
        }

        if (this.adsLoader) {
            this.adsLoader.destroy();
        }
    }
}

// Make class globally available
if (typeof window !== 'undefined') {
    window.LS_AdvertisementManager = LS_AdvertisementManager;
}